import { useEffect, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { MapPin, X, Crosshair, Loader2 } from "lucide-react";
import logger from "../lib/logger";

/**
 * Modal letting an admin set the (latitude, longitude) and optional street
 * address of a site. Provides "Use my current location" via the browser's
 * geolocation API.
 */
export default function SiteLocationDialog({ site, onClose, onSaved }) {
  const [lat, setLat] = useState(site?.latitude ?? "");
  const [lng, setLng] = useState(site?.longitude ?? "");
  const [address, setAddress] = useState(site?.address ?? "");
  const [sla, setSla] = useState(site?.sla_min_photos_per_week ?? "");
  const [busy, setBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  useEffect(() => {
    setLat(site?.latitude ?? "");
    setLng(site?.longitude ?? "");
    setAddress(site?.address ?? "");
    setSla(site?.sla_min_photos_per_week ?? "");
  }, [site]);

  if (!site) return null;

  const useCurrent = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setGeoBusy(false);
        toast.success("Pinned your current location");
      },
      (err) => {
        logger.error(err);
        setGeoBusy(false);
        toast.error("Could not read your location — check browser permissions");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const body = {
        latitude: lat === "" ? null : Number(lat),
        longitude: lng === "" ? null : Number(lng),
        address: address || null,
        sla_min_photos_per_week: sla === "" ? null : Math.max(0, Number(sla) || 0),
      };
      if (body.latitude !== null && (isNaN(body.latitude) || body.latitude < -90 || body.latitude > 90)) {
        toast.error("Latitude must be between -90 and 90");
        setBusy(false);
        return;
      }
      if (body.longitude !== null && (isNaN(body.longitude) || body.longitude < -180 || body.longitude > 180)) {
        toast.error("Longitude must be between -180 and 180");
        setBusy(false);
        return;
      }
      await api.patch(`/sites/${site.id}`, body);
      toast.success("Location saved");
      onSaved?.();
      onClose();
    } catch (err) {
      logger.error("Save location failed:", err);
      toast.error("Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-gray-300 max-w-md w-full p-6 rounded-sm shadow-2xl"
        data-testid="site-location-dialog"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Site location
            </div>
            <h4 className="font-black text-xl tracking-tight">{site.name}</h4>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Team members will only be allowed to sign in within <b>50&nbsp;m</b> of this point.
        </p>

        <button
          type="button"
          onClick={useCurrent}
          disabled={geoBusy}
          data-testid="use-current-location-button"
          className="w-full mb-4 border border-[#002FA7] text-[#002FA7] px-3 py-2 rounded-sm text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#002FA7]/5 disabled:opacity-50"
        >
          {geoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
          {geoBusy ? "Reading location…" : "Use my current location"}
        </button>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Latitude</label>
            <input
              data-testid="site-lat-input"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="-33.8688"
              className="w-full border border-gray-300 px-3 py-2 rounded-sm text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Longitude</label>
            <input
              data-testid="site-lng-input"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="151.2093"
              className="w-full border border-gray-300 px-3 py-2 rounded-sm text-sm font-mono"
            />
          </div>
        </div>

        <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Address (optional)</label>
        <input
          data-testid="site-address-input"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="e.g. 100 George St, Sydney"
          className="w-full border border-gray-300 px-3 py-2 mb-4 rounded-sm text-sm"
        />

        <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
          SLA — Min photos / week (optional)
        </label>
        <input
          data-testid="site-sla-input"
          type="number"
          min={0}
          value={sla}
          onChange={(e) => setSla(e.target.value)}
          placeholder="e.g. 6"
          className="w-full border border-gray-300 px-3 py-2 mb-5 rounded-sm text-sm font-mono"
        />

        <button
          type="submit"
          disabled={busy}
          data-testid="save-location-button"
          className="w-full bg-[#002FA7] text-white py-2.5 font-bold rounded-sm hover:bg-[#002277] disabled:opacity-50"
        >
          {busy ? "SAVING…" : "SAVE LOCATION"}
        </button>
      </form>
    </div>
  );
}
